import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Landmark, Clock, Shield, TrendingUp, ArrowRight, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const capitalProducts = [
  {
    id: 1,
    name: 'SME Trade Finance',
    description: 'Short-term financing for SME trade operations',
    term: '90 days',
    return_rate: 15.0,
    min_investment: 50000,
    funded: 75,
    risk: 'Medium'
  },
  {
    id: 2,
    name: 'Real Estate Bridge Loan',
    description: 'Property development bridge financing',
    term: '12 months',
    return_rate: 18.5,
    min_investment: 100000,
    funded: 45,
    risk: 'Medium-High'
  },
  {
    id: 3,
    name: 'Agricultural Supply Chain',
    description: 'Financing for agricultural value chains',
    term: '6 months',
    return_rate: 14.0,
    min_investment: 25000,
    funded: 90,
    risk: 'Low-Medium'
  },
];

export default function CapitalPage() {
  const { token } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/investments?category=private_debt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data.investments || []);
    } catch (error) {
      console.error('Investments fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout title="Jambo Capital">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Hero */}
        <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border-0">
          <CardContent className="p-6 lg:p-10">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500 flex items-center justify-center">
                <Landmark className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-2">Private Debt & Alternatives</h2>
                <p className="text-muted-foreground max-w-2xl">
                  Access institutional-grade private credit opportunities with attractive risk-adjusted returns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Total AUM</p>
              <p className="text-2xl font-bold">$2.5M</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Avg. Returns</p>
              <p className="text-2xl font-bold text-green-500">15.8%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Active Deals</p>
              <p className="text-2xl font-bold">12</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Default Rate</p>
              <p className="text-2xl font-bold">0.5%</p>
            </CardContent>
          </Card>
        </div>

        {/* Investment Opportunities */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Investment Opportunities</h2>
          <div className="space-y-4">
            {capitalProducts.map((product) => (
              <Card key={product.id} className="card-hover" data-testid={`capital-product-${product.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-lg">{product.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          product.risk === 'Low-Medium' ? 'bg-green-100 text-green-700' :
                          product.risk === 'Medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {product.risk} Risk
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{product.description}</p>
                      
                      <div className="flex items-center gap-6 text-sm mb-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{product.term}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className="text-green-500 font-medium">{product.return_rate}% p.a.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <span>Min. {formatCurrency(product.min_investment)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Funding Progress</span>
                          <span className="font-medium">{product.funded}%</span>
                        </div>
                        <Progress value={product.funded} className="h-2" />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 lg:w-40">
                      <Button className="rounded-full" data-testid={`invest-capital-${product.id}`}>
                        Invest Now
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                      <Button variant="outline" className="rounded-full">
                        <FileText className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it Works */}
        <Card>
          <CardHeader>
            <CardTitle>How Jambo Capital Works</CardTitle>
            <CardDescription>Simple steps to start investing in private debt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: 1, title: 'Browse Deals', description: 'Explore vetted private credit opportunities' },
                { step: 2, title: 'Due Diligence', description: 'Review detailed risk assessments and terms' },
                { step: 3, title: 'Invest', description: 'Commit your capital with secure escrow' },
                { step: 4, title: 'Earn Returns', description: 'Receive regular interest payments' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
